import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import { BeatLoader } from 'react-spinners';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';

import logo from '../../images/logo_report.png';
import Ronin_logo from '../../images/RoninIconSmall.png';
import External_link from '../../images/externalLink.png';
const moment = require('moment-timezone');

const PdfReport = ({ fromDate, toDate, exportFormat = 'pdf', selectedBusiness, selectedSite, siteOptions, isFullReport }) => {
  const [loading, setLoading] = useState(true);
  const report = useSelector(state => state.report);

  const FONT_SIZE_HEADING = 16;
  const FONT_SIZE_TITLE = 12;
  const FONT_SIZE_SUBTITLE = 11;
  const FONT_SIZE_NORMAL = 10;
  const FONT_SIZE_SMALL = 9;
  const FONT_SIZE_XSMALL = 8;

  useEffect(() => {
    if (window.localStorage.getItem('fetch_report') === 'true') {
      setLoading(false);
    }
  }, [report, loading]);

  // Draw dotted line (modern style)
  function drawDottedLine(doc, y) {
    doc.setLineWidth(0.2);
    doc.setDrawColor(150, 150, 150);
    let x = 10;
    while (x < 200) {
        doc.line(x, y, x + 1, y);
        x += 3;
    }
  };

  // Sort Report Data By Keys e.g. Site -> Server -> Camera
  function sortDataByKeys(data, keys) {
    return data.sort((a, b) => {
        for (const key of keys) {                  // loop through each key
        const valA = a[key] ?? '';               // fallback to empty string if key missing
        const valB = b[key] ?? '';
        const compare = valA.localeCompare(valB, 'en', { sensitivity: 'base' }); // case-insensitive
        if (compare !== 0) return compare; // if different, return result
        }
        return 0;  // all keys equal => treat as equal
    });
  };

  // Groups rows of data based on specific fields (key, value). e.g. key: 'Site1'||'Server1'||'Cam1' && value: array of rows that match the key combination
  function groupByKeys(data, keys) {
    const groups = new Map();

    for (const row of data) {
        const key = keys.map(k => row[k]).join("||");  // e.g. key: 'Site1'||'Server1'||'Cam1'
        
        if (!groups.has(key))
            groups.set(key, []);

        groups.get(key).push(row); // push value to key
    }

    return groups;
  };

  // Sort Fault_Start UTC time in chronological order
function sortFaultStartByUTC(keyGroups) {
  for (const row of keyGroups.values()) {
    row.sort((a, b) => new Date(a.Fault_Start) - new Date(b.Fault_Start));
  }
}

function formatUTCDateTime(utcTime) {
    const date = new Date(utcTime);
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

// Optimized timezone conversion
const convertToLocalTime = (utcTime, timezone) => {
  if (timezone && timezone !== 'None') {
      return {
          localTimeformated: moment(utcTime).tz(timezone).format('DD-MM-YYYY HH:mm:ss z'),
          UTCStr: ''
      };
  }
  
  return {
      localTimeformated: formatUTCDateTime(utcTime),
      UTCStr: 'UTC'
  };
};

// Convert each table Fault_Start and Fault_End to local timezone
const convertTableDatesToLocalTimeZone = (reportData) => {
  return reportData.map(row => {
    const startTime = convertToLocalTime(row['Fault_Start'], row['Timezone']);
    let endTime;
    if (row['Fault_End'] === 'continuing') {
      endTime = { localTimeformated: 'continuing', UTCStr: '' };
    } else {
      endTime = convertToLocalTime(row['Fault_End'], row['Timezone']);
    }

    // Return new object without Timezone
    const { Timezone, ...rest } = row;

    return {
      ...rest,
      Fault_Start: `${startTime.localTimeformated} ${startTime.UTCStr}`,
      Fault_End: `${endTime.localTimeformated} ${endTime.UTCStr}`
    };
  });
};

// Function to either show all rows or collapse rows based on faultCountThreshold
function collapseOrFullRender(keyGroups, keys, faultCountThreshold) {
  const outcome = [];
  let final_data = [];
  let freqFaultCount = 0;

  // If user selects full report, don't perform row collapsing based on fault frequency threshold
  if (isFullReport) {
    final_data =  [...keyGroups.values()].flat();
    freqFaultCount = 0;
  }
  // Optimised report
  else {
    for (const row of keyGroups.values()) {     
      // If there are more than 5 faults for a key, check if there is any continuing row
      if (row.length > faultCountThreshold){
        freqFaultCount += 1;
        const continuingRow = row.find(r => r.Fault_End === "continuing");
        // If there is a continuing row, push it to outcome arr
        if (continuingRow) {
            outcome.push({
            ...continuingRow,
            Frequent: true,  // mark collapsed groups
            Offline_Count: row.length,
          });
        }
        // Otherwise sort by Fault_Start to find oldest then push it to outcome arr
        else {
          const oldest = row.reduce((a, b) => 
            new Date(a.Fault_Start) < new Date(b.Fault_Start) ? a : b
          );
          // Push the row with the oldest Fault_Start date
          outcome.push({
            ...oldest,
            Frequent: true,  // mark collapsed groups
            Offline_Count: row.length,
          });
        }
      }
      // If there is less than 5 faults, push everything into the outcome arr
      else {
        // Keep all rows
        for (const r of row) {
            outcome.push({
                ...r,
                Frequent: false,
                Offline_Count: row.length,
            });
        }
      }
    }

    // Represent data using asterisk if fault occurrences exceed 5
    final_data = outcome.map((row) => {
        const newRow = { ...row }; // copy so we don't mutate original
        // Only add asterisk if offlineCount > 5
        if (row.Offline_Count > faultCountThreshold) {
            const deviceKey = keys[keys.length - 1]; // Last element in the keys array
            newRow[deviceKey] = `${row[deviceKey]} *`; // Mark frequent device disconnections
        }
        // Remove unwanted fields for reporting purpose
        const { Offline_Count, Frequent, ...cleanRow } = newRow;
        return cleanRow;
    });
  }

  return {
    finalData: final_data,
    freqFaultCount
  };
}

// Calculate rowspan rows for row group/merge (e.g. Group site names or server names, ...)
function calculateRowSpanArray(data, keys) {
    const spanArr = data.map(() => keys.map(() => 1));
    let spanIndexes = keys.map(() => 0);

    for (let i = 1; i < data.length; i++) {
        for (let j = 0; j < keys.length; j++) {
            if (data[i-1][keys[j]] === data[i][keys[j]]){
                spanArr[spanIndexes[j]][j] += 1;
                spanArr[i][j] = 0; // mark this entry as merged
            }
            else {
                spanIndexes[j] = i;
                // Reset all deeper levels
                for (let k = j+1; k < keys.length; k++){
                    spanIndexes[k] = i;
                }
                break; // No need to check further keys
            }
        }
    }
  return spanArr;
}

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}-${month}-${year}`;
  };

function formatCurrentDateToDDMMYYYYHHMMSS() {
  const currentDate = new Date();
  const day = String(currentDate.getDate()).padStart(2, '0');
  const month = String(currentDate.getMonth() + 1).padStart(2, '0');
  const year = currentDate.getFullYear();

  let hours = currentDate.getHours();
  const minutes = String(currentDate.getMinutes()).padStart(2, '0');
  const seconds = String(currentDate.getSeconds()).padStart(2, '0');

  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const formattedHours = String(hours).padStart(2, '0');

  return `${day}-${month}-${year} ${formattedHours}:${minutes}:${seconds} ${ampm}`;
}

  // Prepare Excel Data
  const prepareData = (title, data) => {
    let finalData = [];
    let freqFaultCountTable = 0;
    let rowSpansArr = [];

    if (data?.length > 0) {
      // Keys Map for sorting and span array
      const keysMap = {
          'Offline Servers': ['Site', 'Server'],
          'Offline Cameras': ['Site', 'Server', 'Camera'],
          'Offline Storages': ['Site', 'Server', 'Storage_ID'],
          'Offline Other Devices': ['Site', 'Server', 'Device_ID']
      };

      const keys = keysMap[title];

      // Sort table data by keys (columns) alphabetically for each table
      const sortedData = keys ? sortDataByKeys(data, keys) : data;

      // Detect system anomaly
      const keyGroups = groupByKeys(sortedData, keys); // For counting the fault occurrencies for any device

      // Sort Fault_Start UTC time in chronological order
      sortFaultStartByUTC(keyGroups);

      const faultCount = 5; // Offline threshold to decide frequent disconnections or not
      let result = collapseOrFullRender(keyGroups, keys, faultCount);

      freqFaultCountTable = result.freqFaultCount;

      // Convert finalData datetime to local timezone
      finalData = convertTableDatesToLocalTimeZone(result.finalData);

      rowSpansArr = calculateRowSpanArray(finalData, keys);

    }
    return {
      finalData,
      freqFaultCount: freqFaultCountTable,
      rowCount: finalData.length,
      rowSpansArr
    };
  };

  // CSV Export Function - Creates separate files and zips them if more than one
  const generateCSV = async () => {
    const fromDateFormatted = formatDate(new Date(fromDate));
    const toDateFormatted = formatDate(new Date(toDate));
    const currentDate = formatCurrentDateToDDMMYYYYHHMMSS();

    // Helper function to create CSV content for a single data type
    const createCSVContent = (title, data) => {
      if (!data || data.length === 0) return null;

      let csvContent = '';
      csvContent += `${title}\n`;
      csvContent += `Business: ${selectedBusiness}\n`;
      csvContent += `Generated at: ${currentDate}\n`;
      csvContent += `Date range: From ${fromDateFormatted} To ${toDateFormatted}\n\n`;

      const {finalData, freqFaultCount, rowSpansArr, rowCount} =  prepareData(title, data);

      // Merging rows
      const transformed_data = finalData.map((row, rowIdx) => {
        const keys = Object.keys(row);
        const newRow = {...row};
        keys.forEach((key, colIdx) => {
            if(rowSpansArr[rowIdx][colIdx] === 0) {
                newRow[key] = '';
            }
        })
        return newRow;
      });
      
      const headers = Object.keys(transformed_data[0]);
      csvContent += headers.join(',') + '\n';      
      
      transformed_data.forEach(row => {
        const values = headers.map(header => {
          const value = row[header];
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        });
        csvContent += values.join(',') + '\n';
      });

      // Append 3 new lines
      csvContent += '\n\n\n';

      if (freqFaultCount > 0 && rowCount > 0) {
        const NEW_LINE = '\r\n';
        csvContent += [
          '(*) The asterisk indicates repeated faults occurred from the previous day.',
          'When more than five similar faults occur, only the earliest incident is shown in this report.',
          'If the fault is still ongoing, only the most recent incident is shown.',
          'For the full list of events, please refer to your VMS client.'
        ].map(line => `"${line}"`).join(NEW_LINE) + NEW_LINE;
      };
    
      return csvContent;
    };

    // Helper function to download a single file
    const downloadFile = (content, filename) => {
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    // Prepare all CSV files
    const dataTypes = [
      { title: 'Offline Servers', data: report.offlineServers, filename: 'offline_servers.csv' },
      { title: 'Offline Cameras', data: report.offlineCameras, filename: 'offline_cameras.csv' },
      { title: 'Offline Storages', data: report.offlineStorage, filename: 'offline_storages.csv' },
      { title: 'Offline Other Devices', data: report.additionalInfo, filename: 'offline_other_devices.csv' }
    ];

    // Create CSV content for all available data types
    const csvFiles = dataTypes
      .map(type => ({
        filename: type.filename,
        content: createCSVContent(type.title, type.data)
      }))
      .filter(file => file.content !== null);

    if (csvFiles.length === 0) {
      alert('No data available to export.');
      return;
    }

    // If only one file, download directly
    if (csvFiles.length === 1) {
      downloadFile(csvFiles[0].content, csvFiles[0].filename);
      return;
    }

    // If multiple files, create a zip
    try {
      const zip = new JSZip();
      
      // Add all CSV files to the zip
      csvFiles.forEach(file => {
        zip.file(file.filename, file.content);
      });

      // Generate the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Download the zip file
      const link = document.createElement('a');
      const url = URL.createObjectURL(zipBlob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'health_reports.zip');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error creating zip file:', error);
      alert('Error creating zip file. Please try again.');
    }
  };

  // Excel Export Function
  const generateExcel = () => {
    const fromDateFormatted = formatDate(new Date(fromDate));
    const toDateFormatted = formatDate(new Date(toDate));
    const currentDate = formatCurrentDateToDDMMYYYYHHMMSS();

    const wb = XLSX.utils.book_new();

    const summaryData = [
      ['Health Alerts Report'],
      ['Business:', selectedBusiness],
      ['Generated at:', currentDate],
      ['Date range:', `From ${fromDateFormatted} To ${toDateFormatted}`],
      []
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    const addDataSheet = (sheetName, data) => {
      if (data && data.length > 0) {
        // Before appending to the workbook, merge rows
        const {finalData, freqFaultCount, rowSpansArr, rowCount} =  prepareData(sheetName, data);
        // Create a worksheet
        const ws = XLSX.utils.json_to_sheet(finalData);

        ws['!merges'] = ws['!merges'] || [];

        // Merging rows
        for (let i = 0; i < rowSpansArr.length; i++) {
          for (let j = 0; j < rowSpansArr[i].length; j++) {
            if (rowSpansArr[i][j] > 1 ) {
              const startRow = i+1;
              const endRow = i+rowSpansArr[i][j];
              ws['!merges'].push({ s: { r: startRow, c: j }, e: { r: endRow, c: j } });
            }
          }
        };

        // Add notes
        if (freqFaultCount > 0 && rowCount > 0) {
          const columnValues = [
            '(*) The asterisk indicates repeated faults occurred from the previous day.',
            '• When more than five similar faults occur, only the earliest incident is shown in this report.',
            '• If the fault is still ongoing, only the most recent incident is shown.',
            '• For the full list of events, please refer to your VMS client.'
          ];
          // Append 3 blank rows first
          XLSX.utils.sheet_add_aoa(ws, [[""], [""], [""]], { origin: -1 });

          // Append each bullet as a new row
          XLSX.utils.sheet_add_aoa(ws, columnValues.map(b => [b]), { origin: -1 });
        }

        // Append the worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      }
    };

    addDataSheet('Offline Servers', report.offlineServers);
    addDataSheet('Offline Storages', report.offlineStorage);
    addDataSheet('Offline Cameras', report.offlineCameras);
    addDataSheet('Offline Other Devices', report.additionalInfo);

    XLSX.writeFile(wb, 'health_report.xlsx');
  };

  // PDF Export Function
  const generatePDF = () => {
    setLoading(true);
    let totalFreqFaultCount = 0;
    const doc = new jsPDF();

    // Define footer height to reserve space
    const FOOTER_HEIGHT = 30;
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const footerStartY = pageHeight - FOOTER_HEIGHT;

    // ===== FOOTER FUNCTION (to be called on every page) =====
    const addFooter = (doc) => {
      const footerY = footerStartY + 5;
      
      // Save current state
      const currentFont = doc.getFont();
      const currentFontSize = doc.getFontSize();
      const currentTextColor = doc.getTextColor();
      
      drawDottedLine(doc, footerY - 6);

      doc.setFontSize(FONT_SIZE_SMALL);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');

      // Line 1
      doc.text(
          'A: Innovation House | Suite 37, 50 Mawson Lakes Blvd, Mawson Lakes SA 5095',
          pageWidth / 2,
          footerY,
          { align: 'center' }
      );

      // Line 2
      doc.text(
          'L#_ 308005  E_ sales@artoflogic.ai  W_ www.artoflogic.ai  ABN_12 644 186 372',
          pageWidth / 2,
          footerY + 5,
          { align: 'center' }
      );

      // Line 3
      doc.setFontSize(FONT_SIZE_SMALL);
      doc.setTextColor(128, 128, 128);
      doc.text(
          '© 2025 Art of Logic Pty Ltd. All rights reserved.',
          pageWidth / 2,
          footerY + 10,
          { align: 'center' }
      );
      
      // Restore previous state
      doc.setFont(currentFont.fontName, currentFont.fontStyle);
      doc.setFontSize(currentFontSize);
      doc.setTextColor(currentTextColor);
    };

    // Add company logo
    doc.addImage(logo, 'PNG', 10, 10, 30, 30);

    // Add Ronin logo on the top right
    doc.addImage(Ronin_logo, 'PNG', 170, 10, 27, 24);

    const currentDate = formatCurrentDateToDDMMYYYYHHMMSS();
    
    // heading
    doc.setFontSize(FONT_SIZE_HEADING);
    doc.setTextColor(205, 92, 92);
    doc.setFont('helvetica', 'bold');
    doc.text('Health Alerts Report', 70, 25);
    
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    let currentY = 45;
    drawDottedLine(doc, currentY - 5);
    doc.setFontSize(FONT_SIZE_SMALL);
    currentY += 3;

    // =============== Section: Business & Sites ================
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(FONT_SIZE_TITLE);
    doc.setTextColor(51, 51, 51);
    doc.text(`Business: ${selectedBusiness}`, 10, currentY);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`The report is generated at ${currentDate}.`, 120, currentY);   
    currentY += 7;
    const fromDateFormatted = formatDate(new Date(fromDate));
    const toDateFormatted = formatDate(new Date(toDate));
    doc.text(`Selected date range: From ${fromDateFormatted} To ${toDateFormatted}.`, 120, currentY);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONT_SIZE_SUBTITLE);
    const sitesText = `Sites Covered: ${selectedSite === 'All' ? siteOptions.length-1 : 1}`;

    doc.text(sitesText, 10, currentY);
    currentY += 8;

    // ===== Section: Summary =====
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    currentY += 6;

    // Summary section
    doc.setFontSize(FONT_SIZE_SUBTITLE);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Fault Summary:', 10, currentY);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONT_SIZE_NORMAL);
    doc.setTextColor(0, 0, 0);

    // generate a table
    const prepareTableData = (title, data) => {
      let freqFaultCountTable = 0;
      let finalData = [];

      if (data?.length > 0) {
        // Keys Map for sorting and span array
        const keysMap = {
            'Offline Servers:': ['Site', 'Server'],
            'Offline Cameras:': ['Site', 'Server', 'Camera'],
            'Offline Storages:': ['Site', 'Server', 'Storage_ID'],
            'Offline Other Devices:': ['Site', 'Server', 'Device_ID']
        };

        const keys = keysMap[title];

        // Sort table data by keys (columns) alphabetically for each table
        const sortedData = keys ? sortDataByKeys(data, keys) : data;

        // Detect system anomaly
        const keyGroups = groupByKeys(sortedData, keys); // For counting the fault occurrencies for any device

        // Sort Fault_Start UTC time in chronological order
        sortFaultStartByUTC(keyGroups);

        const faultCount = 5; // Offline threshold to decide frequent disconnections or not
        let result = collapseOrFullRender(keyGroups, keys, faultCount);

        freqFaultCountTable = result.freqFaultCount;

        // Convert finalData datetime to local timezone
        finalData = convertTableDatesToLocalTimeZone(result.finalData);
      }
      return {
        finalData,
        freqFaultCount: freqFaultCountTable,
        rowCount: finalData.length    // The actual number of rows shown in PDF
      };
    };

    // Generate tables
    const generateTable = (title, data, summaryY) => {
      if (!data || data.length === 0) return; // Early return if no data

      let myColumnStyles = {
        0: { cellWidth:  40},
        1: { cellWidth:  40},
        2: { cellWidth:  40},
        3: { cellWidth:  40},
        4: { cellWidth:  30}
      }

      if (title === 'Offline Cameras:' || title === 'Offline Storages:') {
          myColumnStyles = {
            0: { cellWidth:  35},
            1: { cellWidth:  35},
            2: { cellWidth:  35},
            3: { cellWidth:  30},
            4: { cellWidth:  30},
            5: { cellWidth:  25}
        }
      }

      if (title === 'Offline Other Devices:') {
        myColumnStyles = {
          0: { cellWidth:  30},
          1: { cellWidth:  25},
          2: { cellWidth:  25},
          3: { cellWidth:  20},
          4: { cellWidth:  20},
          5: { cellWidth:  25},
          6: { cellWidth:  25},
          7: { cellWidth:  20}
        }
      }

      // Define keys for row spanning
      const keysMap = {
          'Offline Servers:': ['Site', 'Server'],
          'Offline Cameras:': ['Site', 'Server', 'Camera'],
          'Offline Storages:': ['Site', 'Server', 'Storage_ID'],
          'Offline Other Devices:': ['Site', 'Server', 'Device_ID']
      };
      const keys = keysMap[title];

      // Get spanArr for each table. For merging rows based on column names
      const rowSpansArr = calculateRowSpanArray(data, keys);

      const columns = Object.keys(data[0]);       
      const rows = data.map(item => Object.values(item));

      if (data?.length > 0) {
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(FONT_SIZE_SUBTITLE);
        let titleY = doc.autoTable.previous ? doc.autoTable.previous.finalY + 20 : summaryY + 20;
        const titleHeight = 20;
        // Add a new page if the table title is inside the footer
        if (titleY + titleHeight >= footerStartY) {
          doc.addPage();
          titleY = 20;
        }
        doc.text(title, 10, titleY);
        doc.setFont('helvetica', 'normal');

        // Write table
        doc.autoTable({
          head: [columns],
          body: rows,          
          startY: titleY + 2,
          columnStyles: myColumnStyles,
          theme: 'grid',
          margin: { 
              left: 10, 
              right: 10,
              bottom: FOOTER_HEIGHT
          },
          headStyles: {
            fillColor: [118, 118, 118],
            fontStyle: 'bold',
            fontSize: FONT_SIZE_NORMAL
          },
          bodyStyles: {
              fontSize: FONT_SIZE_SMALL,
              textColor: [0, 0, 0],
              overflow: 'linebreak'
          },
          rowPageBreak: 'avoid',
          didParseCell: function(data) {
            const defaultLineWidth = 0.1;

            if (data.section === 'body') {
              const rowIndex = data.row.index;
              const colIndex = data.column.index;

              // Alternative way:
              if (colIndex < keys.length) {
                const span = rowSpansArr[rowIndex][colIndex];
                // Merged cells
                if (span === 0) {
                  // Empty the cell
                  data.cell.text = [''];
                  
                  // Remove top & bottom borders to make it look merged
                  data.cell.styles.lineWidth = {
                    top: 0,
                    right: defaultLineWidth,
                    // Check if its last row thehn draw bottom border.
                    bottom: (data.section === 'body' && data.row.index === data.table.body.length - 1) ? defaultLineWidth : 0,
                    left: defaultLineWidth
                  };

                // Different site that spans more than 1 row
                } 
                else if (span > 1) {
                  // First cell in group - make it bold
                  data.cell.styles.fontStyle = 'bold';

                  // Remove bottom border
                  data.cell.styles.lineWidth = {
                    top: defaultLineWidth,
                    right: defaultLineWidth,
                    bottom: 0,
                    left: defaultLineWidth
                  };
                }
                // Different site that has no span
                else if (span === 1) {
                  // First cell in group - make it bold
                  data.cell.styles.fontStyle = 'bold';

                  // Keep all borders
                  data.cell.styles.lineWidth = {
                    top: defaultLineWidth,
                    right: defaultLineWidth,
                    bottom: defaultLineWidth,
                    left: defaultLineWidth
                  };
                }
              }
            }
          },
          // Cells are finalised
          didDrawCell: function(data) {
            if (data.section === 'body') {
              const colIndex = data.column.index;
              // Fault categories
              const isLastColumn = colIndex === data.table.columns.length - 1;

              if (isLastColumn) {

                // Current cell value
                const value = String(data.cell.raw || "").toLowerCase();

                const isContinuing = value === 'continuing'; // Is fault continuing?

                // Map table name and continuing status colors
                const circleColors = {
                  "Offline Servers:": {  // if continuing then red, otherwise resolved green
                    fill: isContinuing ? [220, 0, 0] : [0, 150, 0],
                  },      
                  "Offline Cameras:": { // if continuing then yellow, otherwise resolved green 
                    fill: isContinuing ? [230, 150, 0] : [0, 150, 0],
                  },
                  "Offline Storages:": { // if continuing then red, otherwise resolved green
                    fill: isContinuing ? [220, 0, 0] : [0, 150, 0], 
                  },
                  "Offline Other Devices:": { // if continuing then yellow, otherwise resolved green 
                    fill: isContinuing ? [230, 150, 0] : [0, 150, 0],
                  }
                };

                const { x, y, width, height } = data.cell;

                // Draw the filled circle first (light red)
                doc.setFillColor(...circleColors[title].fill);
                doc.circle(
                  x + width + 3,        // x coordinate of circle
                  y + height / 2,       // vertically centered
                  1,                  // radius
                  "F"                   // Fill
                );
              }
              
            }
          },
          // Add footer to each page that the table spans
          didDrawPage: function(data) {
              addFooter(doc);
          },
        });
      }
    };

    // Generate tables for each array
    const serverData = prepareTableData('Offline Servers:', report.offlineServers);
    const storageData = prepareTableData('Offline Storages:', report.offlineStorage);
    const cameraData = prepareTableData('Offline Cameras:', report.offlineCameras);
    const otherDeviceData = prepareTableData('Offline Other Devices:', report.additionalInfo);

    // Total faults
    const totalFaults = serverData.rowCount + cameraData.rowCount + storageData.rowCount + otherDeviceData.rowCount;
    let summaryY = currentY + 7;
    
    if (totalFaults === 0) {
        doc.setTextColor(0, 128, 0);
        doc.text('All systems operational - no faults detected.', 15, summaryY);
        summaryY += 7;
    } else {
        doc.setTextColor(255, 0, 0);
        doc.text(`${totalFaults} faults detected across your infrastructure:`, 15, summaryY);
        summaryY += 7;
        
        if (serverData.rowCount > 0) {
            doc.text(`• Servers: ${serverData.rowCount} events`, 20, summaryY);
            summaryY += 6;
        }
        if (cameraData.rowCount > 0) {
            doc.text(`• Cameras: ${cameraData.rowCount} events`, 20, summaryY);
            summaryY += 6;
        }
        if (storageData.rowCount > 0) {
            doc.text(`• Storage: ${storageData.rowCount} events`, 20, summaryY);
            summaryY += 6;
        }
        if (otherDeviceData.rowCount > 0) {
            doc.text(`• Other Devices: ${otherDeviceData.rowCount} events`, 20, summaryY);
            summaryY += 6;
        }

        let criticalIssues = 0;
        let otherIssues = 0;
        let resolvedIssues = 0;

        const sources = [
          { data: serverData.finalData, type: 'critical' },
          { data: storageData.finalData, type: 'critical' },
          { data: cameraData.finalData, type: 'other' },
          { data: otherDeviceData.finalData, type: 'other' }
        ];

        // Calculations for critical issues, other issues and auto resolved issues
        for (const { data, type } of sources) {
          for (const row of data) {
            if (row.Outage === 'continuing') {
              type === 'critical' ? criticalIssues++ : otherIssues++;
            } else {
              resolvedIssues++;
            }
          }
        }

        // Critical issues
        const rect_width = 50;
        const rect_height = 30;
        const rect_gap = 30;
        const rect_details = {
          "critical_issues": {
            title: 'CRITICAL ISSUES OBSERVED',
            fill: [255, 245, 245],
            stroke: [220, 0, 0],
            total: criticalIssues,
            subtitle: [
              "Ongoing Server and Storage",
              "faults are considered critical"
            ]
          },
          "other_issues": {
            title: 'OTHER ISSUES OBSERVED',
            fill: [255, 250, 230],
            stroke: [230, 150, 0],
            total: otherIssues,
            subtitle: [
              "Ongoing Camera and Other",
              "Device faults"
            ], 
          },
          "auto_resolved": {
            title: 'ISSUES AUTO RESOLVED',
            fill: [245, 255, 245],
            stroke: [0, 150, 0],
            total: resolvedIssues,
            subtitle: [
              "Successfully resolved without",
              "manual intervention"
            ],
          },
        };

        // Draw the 3 rectangles with details
        Object.values(rect_details).forEach((r, idx) => {
          const curr_rect_X = 20 + rect_gap * (idx * 2);

          // Draw the filled rectangle
          doc.setFillColor(...r.fill);
          doc.roundedRect(curr_rect_X, summaryY, rect_width, rect_height, 2, 2, "F");
          
          // Draw the stroke of rectangle
          doc.setDrawColor(...r.stroke);
          doc.setLineWidth(0.4);
          doc.roundedRect(curr_rect_X, summaryY, rect_width, rect_height, 2, 2, "S");

          // Draw the filled circle first (same color as stroke)
          doc.setFillColor(...r.stroke);
          doc.circle(curr_rect_X + 5, summaryY + 4, 1, "F");

          // Title
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(FONT_SIZE_XSMALL);
          doc.setTextColor(...r.stroke);
          doc.text(r.title, curr_rect_X + 8, summaryY + 5);

          // Total
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(30);
          doc.setTextColor(...r.stroke);
          const centerOffsetX = (rect_width - doc.getTextDimensions(r.total).w) / 2;
          doc.text(`${r.total}`, curr_rect_X + centerOffsetX, summaryY + 17);

          // Subtitle
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(r.subtitle, curr_rect_X + 5, summaryY + 24);
        });

        summaryY += 30;
    }

    // Add Ronin link below Fault Summary
    const linkStartX = 50;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONT_SIZE_NORMAL);
    doc.setTextColor(75, 85, 99);
    const linkText = "For live updates and an interactive dashboard, visit the  "; 
    doc.text(linkText, linkStartX, summaryY + 10);    
    const text_width = doc.getTextWidth(linkText);

    doc.setTextColor(0, 102, 204); // blue
    doc.textWithLink("Ronin Portal", linkStartX + text_width, summaryY + 10, { url: 'https://ronin.artoflogic.ai' });
    const link_width = doc.getTextWidth("Ronin Portal");
    doc.setDrawColor(0, 102, 204);
    doc.setLineWidth(0.1);
    
    doc.addImage(External_link, 'PNG', (linkStartX + 1) + text_width + link_width, summaryY + 7, 3, 3);
    doc.line(linkStartX + text_width, summaryY + 11, linkStartX + text_width + link_width, summaryY + 11 ); // Underline

    let noteY = summaryY;

    // Render the tables
    generateTable('Offline Servers:', serverData.finalData, summaryY);
    generateTable('Offline Storages:', storageData.finalData, summaryY);
    generateTable('Offline Cameras:', cameraData.finalData, summaryY);
    generateTable('Offline Other Devices:', otherDeviceData.finalData, summaryY);

    // Total count of frequent disconnection events across all tables
    totalFreqFaultCount = serverData.freqFaultCount + cameraData.freqFaultCount + storageData.freqFaultCount + otherDeviceData.freqFaultCount;

    let finalTableY = doc.autoTable.previous ? doc.autoTable.previous.finalY : noteY;

    // Place the asterisk definition below the last table 
    if (totalFaults > 0 && totalFreqFaultCount > 0) {        
      doc.setTextColor(0, 0, 0);
      finalTableY += 10;
      const asteriskBlockHeight = 32; // actually 25 but adding extra space just in case
      // Check if the note Y is inside the footer, yes move to next page
      if (finalTableY + asteriskBlockHeight >= footerStartY) {
        doc.addPage(); // Add a new page
        finalTableY = 20; 
      }

      // Set font style and size
      doc.setFont("helvetica", "normal");
      doc.setFontSize(FONT_SIZE_SMALL);
      doc.setTextColor(0, 0, 0); // black
      doc.text("(*) The asterisk indicates repeated faults occurred from the previous day.", 10, finalTableY);
      finalTableY += 7;

      const noteDotPoints = [
      '• When more than five similar faults occur, only the earliest incident is shown in this report.', 
      '• If the fault is still ongoing, only the most recent incident is shown.',
      '• For the full list of events, please refer to your VMS client.'
      ];

      noteDotPoints.forEach((text) => {
        doc.text(text, 15, finalTableY);
        finalTableY += 6;
      });
    }

    // Add footer to the first/current page if not already added by autoTable
    if (!doc.autoTable.previous) {
        addFooter(doc);
    }

    doc.save('health_report.pdf');
    doc.autoTable.previous = null;
    setLoading(false);
  };

  // Handle download based on selected format
  const handleDownload = async () => {
    switch(exportFormat) {
      case 'pdf':
        generatePDF();
        break;
      case 'excel':
        generateExcel();
        break;
      case 'csv':
        await generateCSV();
        break;
      default:
        generatePDF();
    }
  };

  // Get button text and variant based on format
  const getButtonConfig = () => {
    switch(exportFormat) {
      case 'pdf':
        return { text: 'Download PDF', variant: 'dark' };
      case 'excel':
        return { text: 'Download Excel', variant: 'success' };
      case 'csv':
        return { text: 'Download CSV (Zip)', variant: 'info' };
      default:
        return { text: 'Download Report', variant: 'dark' };
    }
  };

  const buttonConfig = getButtonConfig();

  return (
    loading ? 
      <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
        <BeatLoader size={10} color="black" />
      </div>
      :
      <div style={{ marginTop: '1rem' }}>
        <Button 
          variant={buttonConfig.variant} 
          onClick={handleDownload} 
          className='download-pdf-btn'
        >
          {buttonConfig.text}
        </Button>
      </div>
  );
};

export default PdfReport;